'use client';

import React from 'react';
import Link from 'next/link';

import { FolderLayout, MediaAdornment, type Entry } from '@hub/common/src/types/cit-public-api';

import { getEntryLink } from '@hub/common/src/navigationUtils';
import { getPrimaryThumbnailPath } from '@hub/common/src/media-utils';
import { addTestThemeToHref } from '@hub/common/src/navigationUtils';

import { useCitOriginClient } from './CitOriginClientProvider';
import { useCitThemeClient } from './CitThemeProvider';
import { getDescription } from '@hub/common/src/properties';

export default function EntryCard({
  entry,
  searchQuery,
  classificationId,
  folderId,
  imageSize,
  textBoxHeight,
  mediaAddornment,
  cardPadding,
  entryCardColor: entryCardColorProp,
  handleShowToolTip,
  handleHideToolTip,
  fromAllSearch,
  fromAccountSearch
}: {
  entry: Entry;
  searchQuery?: string | null;
  classificationId?: string;
  folderId?: string;
  imageSize?: number;
  textBoxHeight?: number;
  mediaAddornment?: MediaAdornment;
  cardPadding?: number;
  entryCardColor?: string;
  handleShowToolTip: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  handleHideToolTip: () => void;
  fromAllSearch?: boolean;
  fromAccountSearch?: boolean;
}) {
  const citOrigin = useCitOriginClient();

  const { accountTheme } = useCitThemeClient();

  const [slot1, slot2, slot3, slot4] = accountTheme.getThumbnailSlotValues(entry);

  const folderLayout = accountTheme.folderLayout();

  const imagePath = getPrimaryThumbnailPath(entry?.media, accountTheme.iconURL() || undefined);
  const bodyFont = accountTheme.bodyFont();

  const lineHeight = accountTheme.lineHeight();
  const letterSpacing = accountTheme.letterSpacing();

  const entryCardColor = entryCardColorProp;

  return (
    <Link
      key={entry.id}
      href={addTestThemeToHref(
        // include the search query in the url if it exists, so that the entry carousel can use it
        searchQuery
          ? getEntryLink(
              entry.slug || entry.id,
              citOrigin,
              entry.account_slug || entry.account_id,
              classificationId,
              folderId,
              fromAccountSearch,
              fromAllSearch
            ) + `?query=${searchQuery}`
          : getEntryLink(
              entry.slug || entry.id,
              citOrigin,
              entry.account_slug || entry.account_id,
              classificationId,
              folderId,
              fromAccountSearch,
              fromAllSearch
            ),
        accountTheme.isTestTheme() ? accountTheme.getCompressedThemeConfig() : undefined
      )}
      className={`hover:opacity-90 transition-opacity h-full ${mediaAddornment === MediaAdornment.ROUNDED ? 'rounded-lg' : 'rounded-none'}`}
      onMouseEnter={handleShowToolTip}
      onMouseLeave={handleHideToolTip}
      data-tooltip-content={slot1}
      data-tooltip-id={entry.id || entry.slug}
      // should be false so page does not scroll to top when modal is opened
      scroll={false}
      prefetch={false}
      style={{
        backgroundColor: entryCardColor,
        padding: cardPadding ? `${cardPadding}px` : undefined
      }}
    >
      {folderLayout === FolderLayout.GRID ? (
        <div className='h-full flex flex-col items-center'>
          <div
            style={{
              width: imageSize ? `${imageSize}px` : '100%',
              height: imageSize ? `${imageSize}px` : 'auto',
              aspectRatio: imageSize ? undefined : '1'
            }}
            className='relative'
          >
            <img
              src={imagePath}
              alt={slot1}
              className={`absolute z-10 ${
                mediaAddornment === 'rounded' ? 'rounded-lg' : 'rounded-none'
              }`}
              loading='lazy'
            />
          </div>
          <div className='w-full' style={{ height: textBoxHeight ? `${textBoxHeight}px` : 'auto' }}>
            {slot1 ? (
              <h6 className='slot1' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
                {slot1}
              </h6>
            ) : null}
            {slot2 ? (
              <p className='slot2' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
                {slot2}
              </p>
            ) : null}
            {slot3 ? (
              <p className='slot3' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
                {slot3}
              </p>
            ) : null}
            {slot4 ? (
              <p className='slot4' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
                {slot4}
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className='w-full h-full flex justify-between gap-4'>
          <div className='flex gap-4 flex-1 min-w-0'>
            <div
              style={{
                minWidth: imageSize ? `${imageSize}px` : '100%',
                height: imageSize ? `${imageSize}px` : 'auto',
                aspectRatio: imageSize ? undefined : '1'
              }}
              className='relative flex-shrink-0'
            >
              <img
                src={imagePath}
                alt={slot1}
                className={`absolute z-10 ${
                  mediaAddornment === 'rounded' ? 'rounded-lg' : 'rounded-none'
                }`}
                loading='lazy'
              />
            </div>
            <div className='h-full flex flex-col'>
              {slot1 ? (
                <h6
                  className='slot1 line-clamp-1'
                  style={{ fontFamily: bodyFont, lineHeight, letterSpacing, whiteSpace: 'normal' }}
                >
                  {slot1}
                </h6>
              ) : null}
              {slot3 ? (
                <p
                  className='slot3 line-clamp-1'
                  style={{ fontFamily: bodyFont, lineHeight, letterSpacing, whiteSpace: 'normal' }}
                >
                  {slot3}
                </p>
              ) : null}
              {slot4 ? (
                <p
                  className='slot4 line-clamp-1'
                  style={{ fontFamily: bodyFont, lineHeight, letterSpacing, whiteSpace: 'normal' }}
                >
                  {slot4}
                </p>
              ) : null}
              <p
                className='slot2 line-clamp-2'
                style={{ fontFamily: bodyFont, lineHeight, letterSpacing, whiteSpace: 'normal' }}
              >
                {getDescription(entry.properties)}
              </p>
            </div>
          </div>

          {slot2 ? (
            <p className='slot2' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
              {slot2}
            </p>
          ) : null}
        </div>
      )}
    </Link>
  );
}
